import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { DayLoad } from '@/shared/api/types';

import styles from './Learning.module.css';

const day = 24 * 60 * 60 * 1000;
const gap = 3;
const minCell = 11;
const maxCell = 15;
const gutter = 28;
const header = 18;
const edge = 8;
const tooltipRoom = 48;

interface Hover {
  text: string;
  x: number;
  top: number;
  bottom: number;
}

function level(reviews: number, busiest: number): number {
  return reviews === 0 ? 0 : Math.max(1, Math.ceil((reviews / busiest) * 4));
}

function minutesOf(seconds: number): string {
  return seconds > 0 && seconds < 60 ? '< 1' : String(Math.round(seconds / 60));
}

/**
 * A year of study days in the manner of the GitHub contribution graph: a
 * column per week starting on Monday, month names above, weekday names on the
 * left, and a tooltip over the hovered or tapped day. There is no streak
 * counter: a missed day is not a failure (PRD, F2). One hue from light to dark
 * encodes the number of answers. Cells fill the width on a wide screen and
 * keep a legible size on a phone, where the weeks scroll under fixed weekday
 * names and open on the latest ones. Screen readers get the active days as a
 * list.
 */
export function ActivityHeatmap({
  days,
  since,
}: {
  days: DayLoad[];
  since: string;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const tooltip = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<Hover | null>(null);
  const [shift, setShift] = useState(0);
  const [below, setBelow] = useState(false);

  useLayoutEffect(() => {
    const element = scroller.current;
    if (!element) {
      return undefined;
    }

    setWidth(element.clientWidth + gutter);
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      setWidth(element.clientWidth + gutter);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const element = tooltip.current;
    if (!hover || !element) {
      return;
    }

    const half = element.offsetWidth / 2;
    const left = Math.min(Math.max(hover.x - half, edge), window.innerWidth - edge - 2 * half);
    setShift(left + half - hover.x);
    setBelow(hover.top < tooltipRoom);
  }, [hover]);

  useEffect(() => {
    const element = scroller.current;
    const hide = (): void => {
      setHover(null);
    };

    element?.addEventListener('scroll', hide);
    window.addEventListener('scroll', hide);

    return () => {
      element?.removeEventListener('scroll', hide);
      window.removeEventListener('scroll', hide);
    };
  }, []);

  const byDate = new Map(days.map((entry) => [entry.date, entry]));
  const busiest = Math.max(1, ...days.map((entry) => entry.reviews));
  const first = Date.parse(`${since}T00:00:00Z`);
  const offset = (new Date(first).getUTCDay() + 6) % 7;
  const start = first - offset * day;
  const total = offset + 365;
  const weeks = Math.ceil(total / 7);
  const cell = Math.min(maxCell, Math.max(minCell, (width - gutter) / weeks - gap));
  const step = cell + gap;
  const graphWidth = weeks * step;
  const graphHeight = header + 7 * step;

  useEffect(() => {
    const element = scroller.current;
    if (element) {
      element.scrollLeft = element.scrollWidth;
    }
  }, [graphWidth]);

  const dateFormat = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  const monthFormat = new Intl.DateTimeFormat(i18n.language, { month: 'short', timeZone: 'UTC' });
  const weekdayFormat = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    timeZone: 'UTC',
  });

  const describe = (time: number, entry: DayLoad | undefined): string =>
    !entry || entry.reviews === 0
      ? t('dashboard.heatmapNone', { date: dateFormat.format(new Date(time)) })
      : t('dashboard.heatmapCell', {
          date: dateFormat.format(new Date(time)),
          count: entry.reviews,
          minutes: minutesOf(entry.seconds),
        });

  const months: { x: number; label: string }[] = [];
  let lastMonth = -1;
  for (let week = 0; week < weeks; week += 1) {
    const weekStart = new Date(start + week * 7 * day);
    const month = weekStart.getUTCMonth();
    if (month !== lastMonth && weekStart.getUTCDate() <= 7) {
      const x = week * step;
      if (months.length === 0 || x - (months.at(-1)?.x ?? 0) >= 3 * step) {
        months.push({ x, label: monthFormat.format(weekStart) });
      }
      lastMonth = month;
    }
  }

  const show = (event: MouseEvent<SVGRectElement>, text: string): void => {
    const box = event.currentTarget.getBoundingClientRect();
    setShift(0);
    setHover({ text, x: box.left + box.width / 2, top: box.top, bottom: box.bottom });
  };

  const active = days
    .filter((entry) => entry.reviews > 0)
    .map((entry) => ({ entry, time: Date.parse(`${entry.date}T00:00:00Z`) }));

  return (
    <figure
      className={styles.figure}
      aria-label={t('dashboard.heatmapSummary', { count: active.length })}
    >
      <div className={styles.heatmapRow}>
        <svg width={gutter} height={graphHeight} aria-hidden="true" className={styles.weekdays}>
          {[0, 2, 4].map((row) => (
            <text key={row} x={0} y={header + row * step + cell - 2} className={styles.axisLabel}>
              {weekdayFormat.format(new Date(Date.UTC(2024, 0, 1 + row)))}
            </text>
          ))}
        </svg>
        <div className={styles.heatmapScroll} ref={scroller}>
          <svg
            width={graphWidth}
            height={graphHeight}
            aria-hidden="true"
            onMouseLeave={() => {
              setHover(null);
            }}
          >
            {months.map((month) => (
              <text key={month.x} x={month.x} y={12} className={styles.axisLabel}>
                {month.label}
              </text>
            ))}
            {Array.from({ length: total }, (_, index) => {
              if (index < offset) {
                return null;
              }

              const time = start + index * day;
              const date = new Date(time).toISOString().slice(0, 10);
              const entry = byDate.get(date);
              const text = describe(time, entry);

              return (
                <rect
                  key={date}
                  data-date={date}
                  className={styles[`level${String(level(entry?.reviews ?? 0, busiest))}`]}
                  x={Math.floor(index / 7) * step}
                  y={header + (index % 7) * step}
                  width={cell}
                  height={cell}
                  rx={2}
                  onMouseEnter={(event) => {
                    show(event, text);
                  }}
                  onClick={(event) => {
                    show(event, text);
                  }}
                />
              );
            })}
          </svg>
        </div>
      </div>
      {hover && (
        <div
          ref={tooltip}
          className={[styles.tooltip, below ? styles.tooltipBelow : ''].join(' ')}
          role="tooltip"
          style={
            {
              left: hover.x + shift,
              top: below ? hover.bottom : hover.top,
              '--arrow-shift': `${String(-shift)}px`,
            } as React.CSSProperties
          }
        >
          {hover.text}
        </div>
      )}
      <div className={styles.heatmapLegend} aria-hidden="true">
        {t('dashboard.less')}
        {[0, 1, 2, 3, 4].map((stepLevel) => (
          <span
            key={stepLevel}
            className={[styles.swatch, styles[`level${String(stepLevel)}`]].join(' ')}
          />
        ))}
        {t('dashboard.more')}
      </div>
      <ul className={styles.srOnly}>
        {active.length === 0 ? (
          <li>{t('dashboard.heatmapSummary', { count: 0 })}</li>
        ) : (
          active.map(({ entry, time }) => <li key={entry.date}>{describe(time, entry)}</li>)
        )}
      </ul>
    </figure>
  );
}
